import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LIMITS, ROOM_OPEN_OFFSETS_MIN, type AddedRoomView, type ParentRoomView, type RoomView } from '@kidzim/shared';
import { Badge, Button, Card, Input, Switch } from '../design-system/components';
import { api, type ApiError } from '../lib/api';
import { RoomStatusBadge } from './Overview';

interface Invitee {
  screenName: string;
  parentEmail: string;
}
interface CreatedInvite {
  screenName: string;
  inviteUrl: string;
}

const offsetLabels: Record<number, string> = { 0: 'Now', 5: 'In 5 min', 15: 'In 15 min', 30: 'In 30 min' };

export function Rooms() {
  const [rooms, setRooms] = useState<ParentRoomView[]>([]);
  const [addedRooms, setAddedRooms] = useState<AddedRoomView[]>([]);
  const [archivedRooms, setArchivedRooms] = useState<ParentRoomView[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [name, setName] = useState('');
  const [permanent, setPermanent] = useState(false);
  const [openInMinutes, setOpenInMinutes] = useState<number>(0);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [allowLinks, setAllowLinks] = useState(false);
  const [allowImages, setAllowImages] = useState(false);
  const [invitees, setInvitees] = useState<Invitee[]>([{ screenName: '', parentEmail: '' }]);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ name: string; invites: CreatedInvite[] } | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setRooms((await api<{ rooms: ParentRoomView[] }>('/api/rooms')).rooms);
  }
  async function loadAddedRooms() {
    setAddedRooms((await api<{ rooms: AddedRoomView[] }>('/api/rooms/added')).rooms);
  }
  async function toggleArchived() {
    if (!showArchived) {
      setArchivedRooms((await api<{ rooms: ParentRoomView[] }>('/api/rooms?archived=1')).rooms);
    }
    setShowArchived((v) => !v);
  }
  useEffect(() => {
    load().catch(() => undefined);
    loadAddedRooms().catch(() => undefined);
  }, []);

  function updateInvitee(i: number, patch: Partial<Invitee>) {
    setInvitees((prev) => prev.map((inv, idx) => (idx === i ? { ...inv, ...patch } : inv)));
  }
  function addInvitee() {
    if (invitees.length >= LIMITS.MAX_KIDS_PER_ROOM) return;
    setInvitees((prev) => [...prev, { screenName: '', parentEmail: '' }]);
  }

  async function createRoom(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const cleanInvitees = invitees
      .filter((inv) => inv.screenName.trim())
      .map((inv) => ({ screenName: inv.screenName.trim(), ...(inv.parentEmail.trim() ? { parentEmail: inv.parentEmail.trim() } : {}) }));
    try {
      const body = permanent
        ? { name, permanent: true, invitees: cleanInvitees, policy: { allowLinks, allowImages, allowEmoji: true } }
        : { name, permanent: false, openInMinutes, durationMinutes, invitees: cleanInvitees, policy: { allowLinks, allowImages, allowEmoji: true } };
      const res = await api<{ room: RoomView; invites: CreatedInvite[] }>('/api/rooms', { method: 'POST', body: JSON.stringify(body) });
      setCreated({ name: res.room.name, invites: res.invites });
      setName('');
      setInvitees([{ screenName: '', parentEmail: '' }]);
      await load();
    } catch (err) {
      const e = err as ApiError;
      setError(e.error === 'invalid' ? 'Please check the room details.' : 'Could not create the room.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kz-stack" style={{ gap: 'var(--space-6)' }}>
      <div>
        <span className="kz-eyebrow">Chat rooms</span>
        <h1 style={{ marginTop: 'var(--space-1)' }}>Create a room</h1>
      </div>

      {created && (
        <Card style={{ background: 'var(--safe-50)', borderColor: 'var(--safe-100)' }}>
          <h3 style={{ color: 'var(--safe-700)' }}>"{created.name}" is ready! 🎉</h3>
          <p className="kz-muted" style={{ font: 'var(--type-small)', marginBottom: 'var(--space-3)' }}>
            Copy each buddy's private link and share it with their grown-up.
          </p>
          <div className="kz-stack" style={{ gap: 'var(--space-2)' }}>
            {created.invites.map((inv) => (
              <div key={inv.inviteUrl} className="kz-row" style={{ justifyContent: 'space-between', gap: 'var(--space-2)' }}>
                <span style={{ minWidth: 100 }}><strong>{inv.screenName}</strong></span>
                <input readOnly value={inv.inviteUrl} className="kz-grow kz-mono" style={{ fontSize: 'var(--text-xs)', padding: '6px 10px', border: 'var(--border-width) solid var(--border-default)', borderRadius: 'var(--radius-sm)' }} />
                <Button size="sm" variant="secondary" onClick={() => navigator.clipboard?.writeText(inv.inviteUrl)}>Copy</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <form className="kz-stack" onSubmit={createRoom}>
          <Input label="Room name" value={name} onChange={(e) => setName(e.target.value)} maxLength={LIMITS.MAX_ROOM_NAME_LEN} required />

          <Switch label="Always open (no schedule)" checked={permanent} onChange={setPermanent} />

          {!permanent && (
            <div className="kz-row" style={{ gap: 'var(--space-4)', flexWrap: 'wrap' }}>
              <div>
                <span className="kz-field-label">Opens</span>
                <div className="kz-row" style={{ gap: 'var(--space-1)' }}>
                  {ROOM_OPEN_OFFSETS_MIN.map((m) => (
                    <Button key={m} type="button" size="sm" variant={openInMinutes === m ? 'primary' : 'secondary'} onClick={() => setOpenInMinutes(m)}>
                      {offsetLabels[m]}
                    </Button>
                  ))}
                </div>
              </div>
              <div style={{ width: 140 }}>
                <Input label="Open for (min)" type="number" min={5} max={720} value={durationMinutes} onChange={(e) => setDurationMinutes(Number(e.target.value))} />
              </div>
            </div>
          )}

          <div className="kz-row" style={{ gap: 'var(--space-6)', flexWrap: 'wrap' }}>
            <Switch label="Allow links" checked={allowLinks} onChange={setAllowLinks} />
            <Switch label="Allow pictures" checked={allowImages} onChange={setAllowImages} />
          </div>

          <div>
            <span className="kz-field-label">Invite kids (up to {LIMITS.MAX_KIDS_PER_ROOM})</span>
            <div className="kz-stack" style={{ gap: 'var(--space-2)' }}>
              {invitees.map((inv, i) => (
                <div key={i} className="kz-row" style={{ gap: 'var(--space-2)' }}>
                  <input placeholder="Screen name" value={inv.screenName} onChange={(e) => updateInvitee(i, { screenName: e.target.value })} style={fieldStyle} />
                  <input placeholder="Parent email (optional)" value={inv.parentEmail} onChange={(e) => updateInvitee(i, { parentEmail: e.target.value })} style={fieldStyle} />
                </div>
              ))}
            </div>
            {invitees.length < LIMITS.MAX_KIDS_PER_ROOM && (
              <Button type="button" variant="ghost" size="sm" onClick={addInvitee} style={{ marginTop: 'var(--space-2)' }}>
                + Add another buddy
              </Button>
            )}
          </div>

          {error && <p className="kz-error-text">{error}</p>}
          <Button type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create room'}</Button>
        </form>
      </Card>

      <div className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h2>All rooms</h2>
        <Button variant="ghost" size="sm" onClick={() => toggleArchived().catch(() => undefined)}>
          {showArchived ? 'Hide archived' : 'Show archived'}
        </Button>
      </div>
      <div className="kz-stack">
        {rooms.map((room) => (
          <Link key={room.id} to={`/app/rooms/${room.id}`} style={{ textDecoration: 'none' }}>
            <Card interactive style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="kz-row" style={{ gap: 'var(--space-2)', alignItems: 'baseline' }}>
                <strong>{room.name}</strong>
                {!room.hostedByYou && <Badge tone="neutral">Co-parent's room</Badge>}
              </div>
              <RoomStatusBadge status={room.status} />
            </Card>
          </Link>
        ))}
      </div>

      {showArchived && (
        <div className="kz-stack">
          <h3 className="kz-muted">Archived</h3>
          {archivedRooms.length === 0 ? (
            <p className="kz-muted" style={{ font: 'var(--type-small)' }}>No archived rooms.</p>
          ) : (
            archivedRooms.map((room) => (
              <Link key={room.id} to={`/app/rooms/${room.id}`} style={{ textDecoration: 'none' }}>
                <Card interactive style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.75 }}>
                  <div className="kz-row" style={{ gap: 'var(--space-2)', alignItems: 'baseline' }}>
                    <strong>{room.name}</strong>
                    {!room.hostedByYou && <Badge tone="neutral">Co-parent's room</Badge>}
                  </div>
                  <Badge tone="neutral">Archived</Badge>
                </Card>
              </Link>
            ))
          )}
        </div>
      )}

      {addedRooms.length > 0 && (
        <>
          <h2>Rooms you can chat in</h2>
          <p className="kz-muted" style={{ font: 'var(--type-small)', marginTop: 'calc(-1 * var(--space-3))' }}>
            A grown-up added you to these rooms. Open the chat to join the kids.
          </p>
          <div className="kz-stack">
            {addedRooms.map(({ room, hostName }) => (
              <Card key={room.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{room.name}</strong>
                  {hostName && <span className="kz-muted" style={{ font: 'var(--type-small)' }}> · hosted by {hostName}</span>}
                </div>
                <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
                  <RoomStatusBadge status={room.status} />
                  {room.status !== 'closed' && (
                    <Link to={`/chat/${room.id}`}>
                      <Button size="sm">Open chat</Button>
                    </Link>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const fieldStyle: React.CSSProperties = {
  flex: 1,
  font: 'var(--type-body)',
  border: 'var(--border-width) solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  padding: '9px 12px',
};
